import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import DashboardSidebar from './DashboardSidebar';
import WelcomeTour from '../onboarding/WelcomeTour';
import AnnouncementBanner from '../common/AnnouncementBanner';
import AnnouncementDrawer from '../common/AnnouncementDrawer';
import VerificationPending from '../../pages/VerificationPending';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import useAnnouncements from '../../hooks/useAnnouncements';
import useTeamChatNotifications from '../../hooks/useTeamChatNotifications';
import { Menu } from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications';
import { useBadges } from '../../hooks/useBadges';
import KprosFab from '../kpros/KprosFab';
import KprosPanel from '../kpros/KprosPanel';
import IdleScreensaver from '../common/IdleScreensaver';
import { useUsageTracker } from '../../hooks/useUsageTracker';

function Dashboard() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        return localStorage.getItem('jpro_sidebar_collapsed') === 'true';
    });
    const [showWelcomeTour, setShowWelcomeTour] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const { unreadCount: notificationCount } = useNotifications();
    const { data: badges } = useBadges();

    // Persist collapsed state
    useEffect(() => {
        localStorage.setItem('jpro_sidebar_collapsed', sidebarCollapsed);
    }, [sidebarCollapsed]);

    const { theme } = useTheme();
    const { currentUser } = useAuth();

    // Track active time per dashboard compartment for admin usage analytics.
    useUsageTracker(!!currentUser);

    // Task count for sidebar badge comes from the bundled /badges poll, gated on my_tasks access.
    const hasTasksAccess = currentUser?.allowedFeatures?.includes('my_tasks');
    const taskCount = hasTasksAccess ? (badges?.tasks?.total_active || 0) : 0;
    const location = useLocation();
    const { announcements, unreadCount, dismiss, dismissAll } = useAnnouncements();
    const { totalUnreadCount: teamChatUnreadCount } = useTeamChatNotifications();

    // Close sidebar when clicking outside on mobile
    const handleContentClick = () => {
        if (sidebarOpen && window.innerWidth < 768) {
            setSidebarOpen(false);
        }
    };

    // Close sidebar on route change
    useEffect(() => {
        if (window.innerWidth < 768) {
            setSidebarOpen(false);
        }
        
    }, [location.pathname]);
    
    // Show welcome tour only for new users who haven't seen it
    useEffect(() => {
        const hasSeenTour = localStorage.getItem('jpro_tour_completed');
        
        // Check if the user is new (created within the last 24 hours)
        const isNewUser = () => {
            if (!currentUser || !currentUser.createdAt) return false;
            
            const userCreatedAt = new Date(currentUser.createdAt);
            const now = new Date();
            const oneDayMs = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
            
            return (now - userCreatedAt) < oneDayMs;
        };
        
        if (currentUser && !hasSeenTour && isNewUser()) {
            // Short timeout to ensure dashboard is loaded first
            const timer = setTimeout(() => {
                setShowWelcomeTour(true);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [currentUser]);

    // UI-level hard gate: logged-in users who haven't verified their email
    // see a dedicated verify page and nothing else from the app. Exempts
    // accounts that predate the migration (backfilled as verified) and OAuth
    // accounts (auto-verified on creation).
    if (currentUser && currentUser.email_verified === false) {
        return <VerificationPending />;
    }

    return (
        <div className={`h-screen overflow-hidden flex relative ${
            theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
        }`}>
            {/* Mobile menu button */}
            <button
                onClick={() => setSidebarOpen(true)}
                className="md:hidden fixed top-4 right-4 z-50 p-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
            >
                <Menu size={24} />
            </button>

            {/* Sidebar - fixed position for desktop */}
            <DashboardSidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed} unreadCount={unreadCount} teamChatUnreadCount={teamChatUnreadCount} taskCount={taskCount} onOpenDrawer={() => setDrawerOpen(true)} />
            
            {/* Overlay to close sidebar on mobile when clicking outside */}
            {sidebarOpen && (
                <div 
                    className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
                    onClick={() => setSidebarOpen(false)}
                ></div>
            )}

            {/* Main Content Area - full height */}
            <main
                className="flex-1 min-w-0 h-screen overflow-hidden flex flex-col"
                onClick={handleContentClick}
            >
                <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
                    <Outlet />
                </div>
            </main>

            <AnnouncementBanner announcements={announcements} onDismiss={dismiss} onOpenDrawer={() => setDrawerOpen(true)} />
            
            {/* Welcome tour for first-time users */}
            {showWelcomeTour && (
                <WelcomeTour onClose={() => setShowWelcomeTour(false)} />
            )}

            <KprosFab />
            <KprosPanel />

            <AnnouncementDrawer
                isOpen={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                announcements={announcements}
                onDismiss={dismiss}
                onDismissAll={dismissAll}
            />

            <IdleScreensaver />
        </div>
    );
}

export default Dashboard;